import { nextTick, ref } from 'vue';
import ClientsABMRepository, {
  IOperationClient,
} from '../repositories/clientsABM.repository';
import { debounce } from '../helpers/debounce';

export function useClientOptions(typePolicyTransport: string) {
  const clientSelect = ref<any>(null);
  const clientOptions = ref<IOperationClient[]>([]);
  const clientPage = ref(0);
  const clientHasNextPage = ref(true);
  const clientLoading = ref(false);
  const clientSearch = ref('');
  let clientRequestVersion = 0;
  let loadedClientSearch: string | null = null;
  let clientScrollElement: HTMLElement | null = null;

  const mergeClients = (clients: IOperationClient[]) => {
    const clientsById = new Map(
      clientOptions.value.map((client) => [client.id, client]),
    );
    clients.forEach((client) => clientsById.set(client.id, client));
    clientOptions.value = Array.from(clientsById.values());
  };

  const loadClients = async (reset: boolean = false) => {
    if (clientLoading.value && !reset) return;
    if (!reset && !clientHasNextPage.value) return;

    if (reset) {
      clientPage.value = 0;
      clientHasNextPage.value = true;
      clientOptions.value = [];
    }

    const requestVersion = reset
      ? ++clientRequestVersion
      : clientRequestVersion;
    const nextPage = clientPage.value + 1;
    const requestSearch = clientSearch.value;
    clientLoading.value = true;

    try {
      const response = await ClientsABMRepository.getOperationClients({
        page: nextPage,
        search: requestSearch,
        typePolicyTransport,
      });

      if (requestVersion !== clientRequestVersion) return;

      mergeClients(response.clients);
      clientPage.value = response.pagination.current_page;
      clientHasNextPage.value = response.pagination.has_next_page;
      loadedClientSearch = requestSearch;
    } catch {
      if (requestVersion === clientRequestVersion) {
        clientHasNextPage.value = false;
      }
    } finally {
      if (requestVersion === clientRequestVersion) {
        clientLoading.value = false;
      }
    }
  };

  const handleClientScroll = () => {
    if (!clientScrollElement) return;

    const reachedBottom = (
      clientScrollElement.scrollTop + clientScrollElement.clientHeight
      >= clientScrollElement.scrollHeight - 10
    );

    if (reachedBottom) {
      loadClients();
    }
  };

  const removeClientScrollListener = () => {
    clientScrollElement?.removeEventListener('scroll', handleClientScroll);
    clientScrollElement = null;
  };

  const attachClientScrollListener = async () => {
    await nextTick();
    removeClientScrollListener();
    clientScrollElement = clientSelect.value?.$el
      ?.querySelector('.multiselect__content-wrapper') ?? null;
    clientScrollElement?.addEventListener('scroll', handleClientScroll);
  };

  const debouncedLoadClients = debounce(() => loadClients(true), 300);

  const handleClientOpen = async () => {
    await attachClientScrollListener();
    if (loadedClientSearch !== clientSearch.value) {
      await loadClients(true);
    }
  };

  const handleClientClose = () => {
    removeClientScrollListener();
    clientSearch.value = '';
    debouncedLoadClients.cancel();
  };

  const handleClientSearch = (search: string) => {
    clientSearch.value = search;
    debouncedLoadClients();
  };

  const disposeClientOptions = () => {
    debouncedLoadClients.cancel();
    removeClientScrollListener();
  };

  return {
    clientSelect,
    clientOptions,
    clientLoading,
    loadClients,
    handleClientOpen,
    handleClientClose,
    handleClientSearch,
    disposeClientOptions,
  };
}
